import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Info,
  Power,
  RefreshCw,
  Trash2,
  Upload
} from 'lucide-react';
import {
  activateNodePack,
  deactivateNodePack,
  deleteNodePack,
  deleteNodeRecalculationJob,
  fetchActiveNodePack,
  fetchNodePack,
  fetchNodePacks,
  listNodeRecalculationJobs,
  startNodeRecalculation,
  updateNodePackComplaintFlag,
  uploadNodePack
} from '../../api';
import { invalidateNodeCatalog } from '../../hooks/useNodeCatalog';
import { useCallCardFields } from '../../contexts/CallCardFieldsContext';
import { useToast } from '../../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import DateRangePicker from '../DateRangePicker';
import CallCardFieldsCard from './CallCardFieldsCard';
import ChecklistItemOrderCard from './ChecklistItemOrderCard';
import NodeChartVisibilityCard from './NodeChartVisibilityCard';

const JOB_POLL_ACTIVE_MS = 3000;
const JOB_POLL_IDLE_MS = 15000;

const STATUS_LABELS = {
  draft: 'Черновик',
  active: 'Активен',
  superseded: 'Заменён'
};

const JOB_STATUS_LABELS = {
  queued: 'В очереди',
  running: 'Выполняется',
  completed: 'Завершён',
  failed: 'Ошибка',
  cancelled: 'Отменён'
};

const TYPE_LABELS = {
  triage: 'Triage',
  complaints: 'Жалобы',
  checklist: 'Чек-лист',
  funnel: 'Воронка',
  objections: 'Возражения'
};

const formatDate = (value) => (value ? format(new Date(value), 'dd.MM.yyyy HH:mm') : '—');

const NodeRow = ({ node }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow data-ev="settings.llm_nodes.node_row_toggle" className="cursor-pointer" onClick={() => setExpanded((value) => !value)}>
        <TableCell className="w-8">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="font-mono text-xs">{node.nodeId}</TableCell>
        <TableCell>
          <Badge variant="secondary">{TYPE_LABELS[node.typeId] || node.typeId}</Badge>
        </TableCell>
        <TableCell className="max-w-[280px] truncate font-mono text-xs text-muted-foreground">
          {node.inputFilter || 'none'}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {node.dependencies?.length ? node.dependencies.join(', ') : '—'}
        </TableCell>
        <TableCell className="text-right text-xs">{node.outputs?.length ?? 0}</TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30">
            <div className="space-y-3 py-2">
              {(node.outputs ?? []).map((output) => (
                <div key={output.outputKey} className="space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono">{output.outputKey}</span>
                    <Badge variant="outline">{output.kind}</Badge>
                    {output.label && <span className="text-muted-foreground">{output.label}</span>}
                  </div>
                  {output.values?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {output.values.map((value) => (
                        <Badge key={value.value} variant="secondary" className="font-normal">
                          {value.label || value.value}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {output.items?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {output.items.map((item) => (
                        <Badge key={item.key} variant="secondary" className="font-normal">
                          {item.label || item.key}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const IssueList = ({ issues, severity }) => {
  const filtered = (issues ?? []).filter((issue) => issue.severity === severity);
  if (filtered.length === 0) {
    return null;
  }

  const isError = severity === 'error';

  return (
    <div
      className={`rounded-md border p-3 text-sm ${
        isError ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-900'
      }`}
    >
      <div className="mb-2 flex items-center gap-2 font-medium">
        {isError ? <AlertTriangle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
        {isError ? `Ошибки (${filtered.length})` : `Предупреждения (${filtered.length})`}
      </div>
      <ul className="space-y-1">
        {filtered.map((issue, index) => (
          <li key={`${issue.code}-${issue.nodeId}-${index}`} className="flex gap-2 max-sm:flex-wrap">
            <span className="font-mono text-xs opacity-70">{issue.code}</span>
            <span className="font-mono text-xs opacity-70">{issue.nodeId}</span>
            <span>{issue.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const LlmNodesTab = () => {
  const { toast } = useToast();
  const { refresh: refreshCallCardFields } = useCallCardFields();

  const [activePack, setActivePack] = useState(null);
  const [packs, setPacks] = useState([]);
  const [preview, setPreview] = useState(null);
  const [jobs, setJobs] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [recalcRange, setRecalcRange] = useState(null);

  const isFetching = useRef(false);

  const previewErrors = useMemo(
    () => (preview?.issues ?? []).filter((issue) => issue.severity === 'error').length,
    [preview]
  );

  const load = useCallback(async () => {
    if (isFetching.current) {
      return;
    }

    isFetching.current = true;
    setIsLoading(true);

    try {
      const [active, list] = await Promise.all([fetchActiveNodePack(), fetchNodePacks()]);
      setActivePack(active);
      setPacks(Array.isArray(list) ? list : []);
    } catch {
      toast({
        title: 'Не удалось загрузить пакеты узлов',
        variant: 'destructive'
      });
    } finally {
      isFetching.current = false;
      setIsLoading(false);
    }
  }, [toast]);

  const loadJobs = useCallback(async () => {
    try {
      const list = await listNodeRecalculationJobs();
      setJobs(Array.isArray(list) ? list : []);
    } catch {
      // Пересчёт — вспомогательная панель; молчим, чтобы не спамить тостами.
    }
  }, []);

  useEffect(() => {
    load();
    loadJobs();
  }, [load, loadJobs]);

  const hasActiveJob = jobs.some((job) => job.status === 'queued' || job.status === 'running');

  useEffect(() => {
    const interval = setInterval(loadJobs, hasActiveJob ? JOB_POLL_ACTIVE_MS : JOB_POLL_IDLE_MS);
    return () => clearInterval(interval);
  }, [loadJobs, hasActiveJob]);

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectedFile) {
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadNodePack(selectedFile);
      setPreview(result);
      await load();

      const errors = (result.issues ?? []).filter((issue) => issue.severity === 'error').length;
      toast({
        title: errors > 0 ? 'Пакет загружен с ошибками' : 'Пакет загружен и проверен',
        description: `Узлов: ${result.nodeCount}. Ошибок: ${errors}.`,
        variant: errors > 0 ? 'destructive' : undefined
      });
    } catch (error) {
      toast({
        title: 'Не удалось загрузить пакет',
        description: error?.response?.data?.message || error.message,
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setFileInputKey((value) => value + 1);
    }
  };

  const handleActivate = async () => {
    if (!preview) {
      return;
    }

    setIsActivating(true);
    try {
      await activateNodePack(preview.id);
      // Каталог кэшируется на уровне модуля: без сброса дашборд до перезагрузки страницы
      // рисует карточки прежнего пакета.
      invalidateNodeCatalog();
      // Кандидаты и действующие источники полей карточки считаются по активному пакету.
      refreshCallCardFields();
      toast({ title: 'Пакет активирован' });
      setPreview(null);
      await load();
    } catch (error) {
      toast({
        title: 'Не удалось активировать пакет',
        description: error?.response?.data?.message || error.message,
        variant: 'destructive'
      });
    } finally {
      setIsActivating(false);
      setConfirmActivate(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateNodePack();
      invalidateNodeCatalog();
      refreshCallCardFields();
      toast({
        title: 'Пакет отключён',
        description: 'Новые звонки анализировать нечем — задачи будут падать, пока пакет не активирован.'
      });
      await load();
    } catch (error) {
      toast({ title: 'Не удалось отключить пакет', description: error.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNodePack(id);
      invalidateNodeCatalog();
      toast({ title: 'Пакет удалён' });
      await load();
    } catch (error) {
      toast({
        title: 'Не удалось удалить пакет',
        description: error?.response?.data?.message || error.message,
        variant: 'destructive'
      });
    }
  };

  const handleComplaintFlagChange = async (path) => {
    if (!activePack) {
      return;
    }

    try {
      await updateNodePackComplaintFlag(activePack.id, path);
      // Путь признака жалобы отдаётся каталогом — его тоже надо перечитать.
      invalidateNodeCatalog();
      setActivePack({ ...activePack, complaintFlagPath: path });
      toast({ title: 'Признак жалобы обновлён' });
    } catch (error) {
      toast({ title: 'Не удалось сохранить признак жалобы', description: error.message, variant: 'destructive' });
    }
  };

  const handleStartRecalculation = async () => {
    if (!recalcRange?.from || !recalcRange?.to) {
      toast({ title: 'Выберите период пересчёта', variant: 'destructive' });
      return;
    }

    try {
      await startNodeRecalculation({
        dateFrom: format(recalcRange.from, 'yyyy-MM-dd'),
        dateTo: format(recalcRange.to, 'yyyy-MM-dd')
      });
      toast({
        title: 'Пересчёт запущен',
        description: 'Расшифровки переиспользуются, повторно выполняется только анализ.'
      });
      await loadJobs();
    } catch (error) {
      toast({
        title: 'Не удалось запустить пересчёт',
        description: error?.response?.data?.message || error.message,
        variant: 'destructive'
      });
    }
  };

  const openPreview = async (id) => {
    try {
      setPreview(await fetchNodePack(id));
    } catch (error) {
      toast({ title: 'Не удалось открыть пакет', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {/* ── Активный пакет ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="flex flex-col gap-3 space-y-0 md:flex-row md:items-start md:justify-between md:gap-0">
          <div>
            <CardTitle>Активный пакет узлов</CardTitle>
            <CardDescription>
              По активному пакету анализируются все новые расшифровки звонков.
            </CardDescription>
          </div>
          {activePack && (
            <Button data-ev="settings.llm_nodes.deactivate" variant="outline" size="sm" onClick={handleDeactivate}>
              <Power className="mr-2 h-4 w-4" />
              Отключить
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!activePack && !isLoading && (
            <p className="text-sm text-muted-foreground">
              Пакет не активирован — звонки анализируются прежним пайплайном.
            </p>
          )}

          {activePack && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge>Версия {activePack.version}</Badge>
                <span className="font-medium">{activePack.name}</span>
                <span className="text-muted-foreground">
                  активирован {formatDate(activePack.activatedAt)}
                </span>
                <span className="text-muted-foreground">узлов: {activePack.nodeCount}</span>
              </div>

              <div className="flex flex-wrap gap-1">
                {(activePack.nodes ?? []).map((node) => (
                  <Badge key={node.nodeId} variant="secondary" className="font-normal">
                    {node.nodeId} · {TYPE_LABELS[node.typeId] || node.typeId}
                  </Badge>
                ))}
              </div>

              <div className="max-w-lg space-y-2">
                <Label>Признак жалобы</Label>
                <Select
                  trackId="settings.llm_nodes.complaint_flag"
                  value={activePack.complaintFlagPath || 'none'}
                  onValueChange={(value) => handleComplaintFlagChange(value === 'none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Не задан" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Не использовать</SelectItem>
                    {(activePack.complaintFlagCandidates ?? []).map((path) => (
                      <SelectItem key={path} value={path}>
                        {path}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Этот выход используется разделом «Жалобы» и уведомлением в Mattermost.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Состав графиков стоит сразу за активным пакетом: он описывает, что этот пакет
          производит. Карточка сама скрывается, пока активного пакета нет. */}
      <NodeChartVisibilityCard />

      {/* Порядок пунктов чек-листа — отдельная карточка: перетаскивание внутри списка
          переименований мешало бы полям ввода. Скрывается сама, если чек-листов нет. */}
      <ChecklistItemOrderCard />

      {/* Поля карточки звонка берут значения из выходов этого же пакета. */}
      <CallCardFieldsCard />

      {/* ── Загрузка ───────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Загрузка пакета</CardTitle>
          <CardDescription>
            ZIP-архив с каталогами узлов: по папке на node_id с файлами node.yaml,
            output_description.json и prompt.md.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-wrap items-end gap-3" onSubmit={handleUpload}>
            <div className="min-w-full flex-1 space-y-2 sm:min-w-[280px]">
              <Label htmlFor="node-pack-file">Архив пакета</Label>
              <Input
                key={fileInputKey}
                id="node-pack-file"
                data-ev="settings.llm_nodes.file_select"
                type="file"
                accept=".zip,application/zip"
                onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
              />
            </div>
            <Button type="submit" data-ev="settings.llm_nodes.upload" disabled={isUploading || !selectedFile}>
              <Upload className="mr-2 h-4 w-4" />
              {isUploading ? 'Проверяем…' : 'Загрузить и проверить'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* ── Предпросмотр ───────────────────────────────────────────────── */}
      {preview && (
        <Card>
          <CardHeader className="flex flex-col gap-3 space-y-0 md:flex-row md:items-start md:justify-between md:gap-0">
            <div>
              <CardTitle className="break-words">Предпросмотр: {preview.name}</CardTitle>
              <CardDescription>
                Узлов: {preview.nodeCount}. Статус: {STATUS_LABELS[preview.status] || preview.status}.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button data-ev="settings.llm_nodes.preview_close" variant="ghost" size="sm" onClick={() => setPreview(null)}>
                Закрыть
              </Button>
              {preview.status === 'draft' && (
                <Button data-ev="settings.llm_nodes.activate" size="sm" disabled={previewErrors > 0 || isActivating} onClick={() => setConfirmActivate(true)}>
                  Активировать
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <IssueList issues={preview.issues} severity="error" />
            <IssueList issues={preview.issues} severity="warning" />

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Узел</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Фильтр запуска</TableHead>
                  <TableHead>Зависимости</TableHead>
                  <TableHead className="text-right">Выходов</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(preview.nodes ?? []).map((node) => (
                  <NodeRow key={node.nodeId} node={node} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── История версий ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>История версий</CardTitle>
          <CardDescription>Загруженные пакеты этого тенанта.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Версия</TableHead>
                <TableHead>Имя</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Загружен</TableHead>
                <TableHead className="text-right">Узлов</TableHead>
                <TableHead className="text-right">Замечания</TableHead>
                <TableHead className="w-32" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {packs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    Пакеты ещё не загружались.
                  </TableCell>
                </TableRow>
              )}
              {packs.map((pack) => (
                <TableRow key={pack.id}>
                  <TableCell>{pack.version ?? '—'}</TableCell>
                  <TableCell>{pack.name}</TableCell>
                  <TableCell>
                    <Badge variant={pack.status === 'active' ? 'default' : 'secondary'}>
                      {STATUS_LABELS[pack.status] || pack.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(pack.createdAt)}</TableCell>
                  <TableCell className="text-right">{pack.nodeCount}</TableCell>
                  <TableCell className="text-right text-xs">
                    {pack.errorCount > 0 && <span className="text-red-600">{pack.errorCount} ош.</span>}
                    {pack.errorCount > 0 && pack.warningCount > 0 && ' / '}
                    {pack.warningCount > 0 && <span className="text-amber-600">{pack.warningCount} пред.</span>}
                    {pack.errorCount === 0 && pack.warningCount === 0 && '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-ev="settings.llm_nodes.pack_open" variant="ghost" size="sm" onClick={() => openPreview(pack.id)}>
                      Открыть
                    </Button>
                    {pack.status !== 'active' && (
                      <Button data-ev="settings.llm_nodes.pack_delete" variant="ghost" size="sm" onClick={() => handleDelete(pack.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Пересчёт за период ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Пересчёт за период</CardTitle>
          <CardDescription>
            Прогоняет уже обработанные звонки через активный пакет заново. Распознавание речи
            не повторяется — только анализ.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <DateRangePicker value={recalcRange} onChange={setRecalcRange} />
            <Button data-ev="settings.llm_nodes.recalc_start" onClick={handleStartRecalculation} disabled={!activePack || hasActiveJob}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Запустить
            </Button>
          </div>

          {jobs.length > 0 && (
            <div className="space-y-2">
              {jobs.map((job) => (
                <div key={job.id} className="rounded-md border p-3">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span>{JOB_STATUS_LABELS[job.status] || job.status}</span>
                    <span className="text-muted-foreground">
                      {job.processedRows} / {job.totalRows}
                      {job.failedRows > 0 && ` · пропущено ${job.failedRows}`}
                    </span>
                  </div>
                  <Progress value={job.progress} />
                  {job.error && <p className="mt-2 text-xs text-red-600">{job.error}</p>}
                  {(job.status === 'completed' || job.status === 'failed') && (
                    <Button
                      data-ev="settings.llm_nodes.recalc_job_dismiss"
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={async () => {
                        await deleteNodeRecalculationJob(job.id);
                        await loadJobs();
                      }}
                    >
                      Убрать
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmActivate} onOpenChange={setConfirmActivate}>
        <AlertDialogContent trackId="settings.llm_nodes.activate_dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Активировать пакет?</AlertDialogTitle>
            <AlertDialogDescription>
              Текущий активный пакет будет помечен как заменённый. Новые звонки сразу пойдут
              по загруженному графу узлов; ранее обработанные звонки останутся на прежней версии
              до пересчёта.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-ev="settings.llm_nodes.activate_cancel">Отмена</AlertDialogCancel>
            <AlertDialogAction data-ev="settings.llm_nodes.activate_confirm" onClick={handleActivate}>Активировать</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LlmNodesTab;
